import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Send, MessageCircle, Users, Search, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/layouts/DashboardLayout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth()+1).padStart(2, '0')}/${d.getFullYear()}`;
};

const ChatPage = () => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const type = user?.role === "admin" ? "admin" : "agent";

  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatUsers, setChatUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch {} finally { setLoading(false); }
  }, [token]);

  const fetchMessages = useCallback(async (convoId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convoId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchConversations();
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/chat/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChatUsers(res.data);
      } catch {}
    };
    fetchUsers();
  }, [fetchConversations, token]);

  // Handle pre-filled participant from URL params
  useEffect(() => {
    const participantId = searchParams.get('to');
    const quoteId = searchParams.get('quote_id');
    const quoteNumber = searchParams.get('quote_number');
    if (participantId) {
      const startConvo = async () => {
        try {
          const res = await axios.post(`${API}/chat/conversations`, 
            { participant_id: participantId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setActiveConvo(res.data);
          await fetchMessages(res.data.id);
          if (quoteId && quoteNumber) {
            setNewMessage(`Regarding Quote #${quoteNumber}: `);
          }
        } catch {}
      };
      startConvo();
    }
  }, [searchParams, token, fetchMessages]);

  // Poll for new messages
  useEffect(() => {
    if (activeConvo) {
      pollRef.current = setInterval(() => {
        fetchMessages(activeConvo.id);
        fetchConversations();
      }, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeConvo, fetchMessages, fetchConversations]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    setShowNewChat(false);
    await fetchMessages(convo.id);
  };

  const startNewChat = async (otherUser) => {
    try {
      const res = await axios.post(`${API}/chat/conversations`,
        { participant_id: otherUser.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveConvo(res.data);
      setShowNewChat(false);
      await fetchMessages(res.data.id);
      fetchConversations();
    } catch {}
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConvo || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/conversations/${activeConvo.id}/messages`,
        { text: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      await fetchMessages(activeConvo.id);
      fetchConversations();
    } catch {} finally { setSending(false); }
  };

  const filteredUsers = chatUsers.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const getOtherName = (convo) => convo.other_name || 'Unknown';
  const getOtherRole = (convo) => convo.other_role || '';
  const getUnread = (convo) => convo.unread_count?.[user?.id] || 0;

  return (
    <DashboardLayout type={type}>
      <div className="h-[calc(100vh-120px)] flex bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="chat-page">
        {/* Sidebar - Conversations */}
        <div className={`w-80 border-r border-gray-200 flex flex-col ${activeConvo ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Messages</h2>
              <Button
                size="sm"
                onClick={() => { setShowNewChat(!showNewChat); setActiveConvo(null); }}
                className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3"
                data-testid="new-chat-btn"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> New
              </Button>
            </div>
          </div>

          {showNewChat ? (
            <div className="flex-1 overflow-y-auto">
              <div className="p-3">
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="user-search"
                />
              </div>
              <div className="space-y-0.5 px-2">
                {filteredUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => startNewChat(u)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left transition-colors"
                    data-testid={`chat-user-${u.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-emerald-700">{u.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.company_name || u.role}</p>
                    </div>
                    <Badge className="ml-auto text-[10px] bg-gray-100 text-gray-500">{u.role}</Badge>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No conversations yet</p>
                  <p className="text-xs text-gray-300 mt-1">Click "New" to start chatting</p>
                </div>
              ) : (
                <div className="space-y-0.5 p-2">
                  {conversations.map(convo => {
                    const unread = getUnread(convo);
                    const isActive = activeConvo?.id === convo.id;
                    return (
                      <button
                        key={convo.id}
                        onClick={() => openConversation(convo)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          isActive ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`convo-${convo.id}`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isActive ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <span className="text-sm font-bold">{getOtherName(convo)[0]?.toUpperCase()}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                              {getOtherName(convo)}
                            </p>
                            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                              {formatDate(convo.last_message_at)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className={`text-xs truncate ${unread > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                              {convo.last_message || 'No messages yet'}
                            </p>
                            {unread > 0 && (
                              <span className="ml-2 bg-emerald-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {unread}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Chat Area */}
        <div className={`flex-1 flex flex-col ${!activeConvo ? 'hidden md:flex' : 'flex'}`}>
          {activeConvo ? (
            <>
              {/* Chat Header */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
                <button
                  onClick={() => setActiveConvo(null)}
                  className="md:hidden p-1 rounded hover:bg-gray-100"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-emerald-700">{getOtherName(activeConvo)[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{getOtherName(activeConvo)}</p>
                  <p className="text-xs text-gray-400">{activeConvo.other_company || getOtherRole(activeConvo)}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50/50">
                {messages.length === 0 && (
                  <div className="text-center py-12 text-gray-300">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Start the conversation</p>
                  </div>
                )}
                {messages.map((msg, idx) => {
                  const isMine = msg.sender_id === user?.id;
                  const showDate = idx === 0 || formatDate(messages[idx - 1]?.created_at) !== formatDate(msg.created_at);
                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex justify-center my-3">
                          <span className="text-[10px] text-gray-400 bg-white px-3 py-1 rounded-full border border-gray-100">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                          isMine
                            ? 'bg-emerald-600 text-white rounded-br-md'
                            : 'bg-white text-gray-800 border border-gray-100 rounded-bl-md shadow-sm'
                        }`}>
                          {msg.quote_number && (
                            <p className={`text-[10px] font-bold mb-1 ${isMine ? 'text-emerald-200' : 'text-emerald-600'}`}>
                              Re: Quote #{msg.quote_number}
                            </p>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                          <p className={`text-[10px] mt-1 ${isMine ? 'text-emerald-200' : 'text-gray-300'} text-right`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={sendMessage} className="px-5 py-3 border-t border-gray-100 flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-10 bg-gray-50 border-gray-200"
                  data-testid="chat-input"
                  autoFocus
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-emerald-600 hover:bg-emerald-700 h-10 px-4"
                  data-testid="send-msg-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-300">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium text-gray-400">Select a conversation</p>
                <p className="text-sm text-gray-300 mt-1">or start a new chat</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatPage;
